/**
 * What staff see for each sign-in error: the "Errors and what the user sees" table in the
 * sign-in spec (`OT9th` in docs/design/mothership.pen). Codes are better-auth's and ours
 * (packages/auth).
 */

export interface Problem {
  /** Shown under the password field. */
  password?: string;
  /** A red alert above the form. */
  alert?: { title: string; body: string };
  /** The account is locked: Sign in stays disabled for this email. */
  locked?: boolean;
}

const ASK_ADMIN = "ask an administrator to resend your invitation";
const FORGOT = "choose a new one with Forgot your password";

export function signInProblem(code: string, email: string): Problem {
  switch (code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return { password: "That email and password don't match." };
    case "STAFF_PIN_NOT_ALLOWED":
      return { password: "Sign in to the admin console with your password, not a PIN." };
    case "ACCOUNT_LOCKED":
      return {
        locked: true,
        alert: {
          title: "Too many wrong attempts",
          body: `Try again in 15 minutes. If you have forgotten your password, ${FORGOT}.`,
        },
      };
    case "NO_PROFILE_FOR_APP":
      return {
        alert: {
          title: "No access to the console",
          body: `${email || "This account"} isn't staff in any company. An administrator in your company can add you. Drivers sign in with the driver app.`,
        },
      };
    case "TOO_MANY_REQUESTS":
    case "HTTP_429": // better-auth's own rate limit carries no code
      return {
        alert: {
          title: "Too many sign-in attempts",
          body: "Too many attempts from this network. Wait a minute and try again.",
        },
      };
    case "NETWORK_ERROR":
      return {
        alert: {
          title: "No connection",
          body: "The console can't reach MajiTap. Check your connection and try again.",
        },
      };
    default:
      return { alert: { title: "Sign-in failed", body: "Something went wrong on our side. Try again in a moment." } };
  }
}

/** `reset`: the link came from Forgot password (or an administrator), not an invitation. */
export function setPasswordProblem(code: string, message: string, reset = false): Problem {
  switch (code) {
    case "INVALID_TOKEN":
      return {
        alert: {
          title: "This link has expired",
          body: reset
            ? "A reset link works for an hour and only once. Ask for a new one with Forgot your password on the sign-in page."
            : `The link in your invitation works for 72 hours and only once. ${ASK_ADMIN[0].toUpperCase()}${ASK_ADMIN.slice(1)}.`,
        },
      };
    case "PASSWORD_TOO_SHORT":
      return { password: message };
    case "PIN_MUST_BE_4_DIGITS":
      return { password: "Use at least 8 characters, not only digits." };
    default:
      return signInProblem(code, "");
  }
}

/** Changing your own password on My profile (P8-54). */
export function changePasswordProblem(code: string, message: string): Problem & { current?: string } {
  switch (code) {
    case "INVALID_PASSWORD":
      return { current: "That isn't your current password." };
    case "STAFF_PIN_NOT_ALLOWED":
      return { password: "Use at least 8 characters, not only digits. Staff can't use a 4-digit PIN." };
    default:
      return setPasswordProblem(code, message);
  }
}

/** Client-side checks before the password is sent: the same rules the server applies to staff. */
export function passwordProblem(password: string): string | undefined {
  if (/^\d+$/.test(password)) return "Use at least 8 characters, not only digits. Staff can't use a 4-digit PIN.";
  if (password.length < 8) return "At least 8 characters.";
  return undefined;
}

export const emailProblem = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? undefined : "Enter the email address your invitation was sent to.";
